import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { Modal } from './ui/Modal';
import { toaster } from './ui/toast';
import { resetPassword } from '../api/samApi';
import { useCapabilities } from '../hooks/useCapabilities';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { canResetPassword, resetPasswordErrorMessage, type ResetTarget } from '../utils/userAdmin';
import { useT, type MessageKey } from '../i18n/admin';

// SAM-683: utils/userAdmin returns English sentences (and the permission check matches on
// them), so they are translated here, at render time. A sentence missing from this table —
// e.g. a 400 message from the server — is shown as it came.
const MESSAGE_KEY: Record<string, MessageKey> = {
  'This is your own account. Use "Forgot password" on the sign-in page.': 'reset.reason.ownAccount',
  'You need user management permission to reset passwords.': 'reset.reason.noPermission',
  'You can only reset passwords of your own business users.': 'reset.reason.otherBusiness',
  'Xhinola accounts cannot be reset from here.': 'reset.reason.xhinola',
  'Only a tenant administrator can reset an administrator.': 'reset.reason.adminOnly',
  'The user is inactive. Activate them first.': 'reset.reason.inactive',
  'The user has not signed in yet, so there is no password to reset.': 'reset.reason.neverSignedIn',
  'Could not reach the server. Check the connection and try again.': 'reset.error.network',
  'Password reset is not available on this environment yet.': 'reset.error.notDeployed',
  "You are not allowed to reset this user's password.": 'reset.error.forbidden',
  'This user no longer exists.': 'reset.error.notFound',
  'The user has no verified email or phone, so Cognito cannot send the reset code.': 'reset.error.noVerifiedContact',
  'Too many resets for this user. Try again later.': 'reset.error.tooMany',
  'The reset request was not accepted.': 'reset.error.rejected',
  'Could not reset the password. Try again.': 'reset.error.generic',
};

/**
 * "Reset password" for a business user: POST /sam/users/{username}/reset-password with
 * `{temporary: true}`. Cognito (AdminResetUserPassword) emails the user a verification code and
 * blocks sign-in with the old password until they set a new one through "Forgot password".
 * No password is chosen or seen by staff.
 */
export const ResetPasswordButton: React.FC<{ target: ResetTarget; displayName: string }> = ({ target, displayName }) => {
  const { groups, callerBusinessId, callerSub, isLoading } = useCapabilities();
  const { email } = useUserPermissions();
  const [open, setOpen] = useState(false);
  const { t } = useT();
  const say = (text: string) => (MESSAGE_KEY[text] ? t(MESSAGE_KEY[text]) : text);

  const mutation = useMutation({
    mutationFn: () => resetPassword(target.username, { temporary: true }),
    onSuccess: () => {
      setOpen(false);
      toaster.positive(t('reset.sent', { who: target.email || displayName }), { autoHideDuration: 4000 });
    },
    onError: (error) => {
      toaster.negative(say(resetPasswordErrorMessage(error)), { autoHideDuration: 6000 });
    },
  });

  if (isLoading) return null;
  const permission = canResetPassword({ groups, callerBusinessId, callerSub, email }, target);
  // No MANAGE_USERS at all: nothing to offer, hide the control.
  if (!permission.allowed && /permission/.test(permission.reason || '')) return null;

  return (
    <>
      <button
        type="button"
        disabled={!permission.allowed}
        title={permission.allowed ? undefined : say(permission.reason || '')}
        onClick={() => setOpen(true)}
        className="h-10 px-3.5 rounded-[10px] border border-solid border-[var(--line)] bg-white text-sm font-semibold cursor-pointer inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <KeyRound size={15} aria-hidden /> {t('reset.button')}
      </button>
      <Modal
        isOpen={open}
        onClose={() => !mutation.isPending && setOpen(false)}
        title={t('reset.title')}
        subtitle={displayName}
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={mutation.isPending}
              className="h-10 px-3.5 rounded-[10px] border border-solid border-[var(--line)] bg-white text-sm font-semibold cursor-pointer"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="h-10 px-3.5 rounded-[10px] border-0 bg-[var(--tenant-primary,#005488)] text-white text-sm font-semibold cursor-pointer disabled:opacity-60"
            >
              {mutation.isPending ? t('reset.sending') : t('reset.send')}
            </button>
          </div>
        }
      >
        <div className="flex flex-col gap-2 text-sm text-[var(--ink-700,#374151)]">
          <p className="m-0">
            {t('reset.bodyEmail')} <strong>{target.email || target.username}</strong>.
          </p>
          <p className="m-0">
            {t('reset.bodyBefore')}{' '}<strong>{t('reset.forgotPassword')}</strong>{t('reset.bodyAfter')}
          </p>
        </div>
      </Modal>
    </>
  );
};
